import { MetricKind, Trend, trendOf } from "./trend";

/*
 * The metrics bar's eight numbers, each with the kind of number it is (which decides how a change is tested: a count, a rate, or an average) and how it is written in the hover card. `current` and
 * `previous` are what getMetricsData answers for the interval on the bar and the interval before it.
 */
export type MetricKey = "pageViews" | "totalSession" | "percentNewSessions" | "pageviewsPerSession" | "bounceRate" | "avgSessionDuration" | "conversions" | "conversionRate";

export const METRIC_KINDS: Record<MetricKey, MetricKind> = {
  pageViews: "count",
  totalSession: "count",
  percentNewSessions: "rate",
  pageviewsPerSession: "average",
  bounceRate: "rate",
  avgSessionDuration: "average",
  conversions: "count",
  conversionRate: "rate",
};

export const METRIC_KEYS = Object.keys(METRIC_KINDS) as MetricKey[];

/** A number from whatever the backend sent: 45.8, "45.8", "45.8%". NaN when there is nothing to read (never 0: a missing number is not a zero). */
export const numberOf = (value: unknown): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.trim() !== "") return parseFloat(value);

  return NaN;
};

/** The trend of each of the eight numbers; null when either interval's numbers are missing altogether. */
export const trendsFor = (current: Record<string, unknown> | null | undefined, previous: Record<string, unknown> | null | undefined): Record<MetricKey, Trend> | null => {
  if (!current || !previous) return null;
  const currentSessions = numberOf(current.totalSession);
  const previousSessions = numberOf(previous.totalSession);

  return Object.fromEntries(
    METRIC_KEYS.map((key) => [key, trendOf({ kind: METRIC_KINDS[key], current: numberOf(current[key]), previous: numberOf(previous[key]), currentSessions, previousSessions })])
  ) as Record<MetricKey, Trend>;
};

const hms = (ms: number): string => {
  const total = Math.max(0, Math.round(ms / 1000));
  const pad = (n: number) => String(n).padStart(2, "0");

  return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
};

/** A previous value written the way the bar writes the current one (counts with thousands, rates with a %, the length as hours:minutes:seconds). */
export const valueText = (key: MetricKey, metrics: Record<string, unknown> | null | undefined): string => {
  const n = numberOf(metrics?.[key]);
  if (!Number.isFinite(n)) return "";
  if (key === "avgSessionDuration") return hms(n);
  if (METRIC_KINDS[key] === "rate") return `${n.toLocaleString("en-US", { maximumFractionDigits: 1 })}%`;
  if (METRIC_KINDS[key] === "average") return n.toLocaleString("en-US", { maximumFractionDigits: 2 });

  return Math.round(n).toLocaleString("en-US");
};
